import React, { useState, useEffect, useRef } from "react";
import { ShieldCheck, ScanFace, Mail, AlertTriangle, Loader2 } from "lucide-react";
import { useI18n } from "i18n";
import LanguageSwitcher from "components/LanguageSwitcher";

// OIDC consent bridge (PUBLIC). After the hosted face capture (/v/:token), the flow returns
// here with ?cid=<company>&token=<session>&status=passed&code=<one-time>. We read the auth
// result, optionally confirm the person's identity by e-mail (on an ambiguous match), show
// the consent (bound to the relying-party app), then hand back the RP redirect (?code=&state=).
//
// Language policy (public screens): the user's browser language when we ship it, else the
// tenant/company language returned by the state endpoint. These endpoints live at the tenant
// root (/<cid>/v2.0/...), NOT under /api — call them by absolute path with a plain fetch.

async function post(path, body) {
  const r = await fetch(path, {
    method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify(body),
  });
  const data = await r.json().catch(() => ({}));
  if (!r.ok) throw Object.assign(new Error(data.error || "error"), { data, status: r.status });
  return data;
}

export default function OidcConsent() {
  const { t, setPublicLanguage } = useI18n();
  const params = new URLSearchParams(window.location.search);
  const cid = params.get("cid");
  const token = params.get("token");
  const status = params.get("status");
  const base = `/${cid}/v2.0/authorize`;

  const scopeLabel = {
    openid: t("oidc.scope.openid", "Identidade (identificador único)"),
    profile: t("oidc.scope.profile", "Nome do perfil"),
    email: t("oidc.scope.email", "Endereço de e-mail"),
  };

  const [state, setState] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);  // [i18nKey, fallback]
  const [otp, setOtp] = useState("");
  const [otpSent, setOtpSent] = useState(false);
  const [emailConfirmed, setEmailConfirmed] = useState(false);

  // Load the authorize state exactly ONCE. It must NOT re-run when the user switches the
  // language (t/setPublicLanguage change identity) — refetching broke the screen, and it
  // would also reset the language the user just picked.
  const loadedRef = useRef(false);
  useEffect(() => {
    if (loadedRef.current) return;
    loadedRef.current = true;
    if (!cid || !token) { setError(["oidc.invalidRequest", "Solicitação inválida."]); setLoading(false); return; }
    (async () => {
      try {
        const r = await fetch(`${base}/state?token=${encodeURIComponent(token)}`);
        const d = await r.json();
        setPublicLanguage(d.language);   // browser lang, else tenant lang (initial only)
        setState(d);
        setEmailConfirmed(!d.needs_email_confirmation);
      } catch {
        setError(["oidc.loadError", "Não foi possível carregar a solicitação."]);
      } finally { setLoading(false); }
    })();
  }, [cid, token, base, setPublicLanguage]);

  const sendOtp = async () => {
    setBusy(true); setError(null);
    try { await post(`${base}/confirm-email`, { token }); setOtpSent(true); }
    catch (e) {
      setError(e.data?.error === "no_email"
        ? ["oidc.noEmail", "Sem e-mail cadastrado para confirmação."]
        : ["oidc.otpSendFailed", "Falha ao enviar o código."]);
    } finally { setBusy(false); }
  };
  const verifyOtp = async () => {
    setBusy(true); setError(null);
    try { await post(`${base}/verify-email`, { token, code: otp.trim() }); setEmailConfirmed(true); }
    catch { setError(["oidc.otpInvalid", "Código inválido ou expirado."]); }
    finally { setBusy(false); }
  };
  // Consent is remembered: once the person authorized this Client App, we don't ask again.
  // When a valid consent already exists and no e-mail step is pending, approve automatically.
  const autoApproved = useRef(false);
  const autoApprove =
    state?.status === "ok" && state.already_consented && !state.needs_email_confirmation;
  useEffect(() => {
    if (autoApprove && !autoApproved.current && !busy && !error) {
      autoApproved.current = true;
      decide(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoApprove]);

  const decide = async (approve) => {
    setBusy(true); setError(null);
    try {
      const d = await post(`${base}/complete`, { token, approve });
      if (d.redirect) { window.location.href = d.redirect; return; }
      setError(["oidc.unexpected", "Resposta inesperada do servidor."]);
    } catch (e) {
      if (e.data?.error === "email_confirmation_required") {
        setError(["oidc.confirmFirst", "Confirme sua identidade por e-mail primeiro."]);
      } else setError(["oidc.completeFailed", "Não foi possível concluir."]);
    } finally { setBusy(false); }
  };

  const box = "min-h-screen flex items-center justify-center bg-[#0e1013] text-slate-100 px-4 py-8";
  const card = "w-full max-w-[440px] rounded-2xl bg-[#161a1f] border border-[#262d36] p-6 shadow-xl";

  if (loading) return <div className={box}><Loader2 className="h-6 w-6 animate-spin text-blue-400" /></div>;

  // Already authorized → skip the approval screen, show a brief "signing you in".
  if (autoApprove && !error) {
    return (
      <div className={box}><div className={card}>
        <div className="flex flex-col items-center text-center gap-3 py-4">
          <Loader2 className="h-7 w-7 animate-spin text-blue-400" />
          <p className="text-sm text-slate-300">
            {t("oidc.signingIn", "Entrando em {app}…").replace("{app}", state.client_name || "")}
          </p>
        </div>
      </div></div>
    );
  }

  // Face matched but the trust tier is too low → inform + recommend how to raise it.
  if (state && state.status === "tier_too_low") {
    const tierName = (k) => t(`bio.${k}`, k);
    const recs = state.recommendations || [];
    return (
      <div className={box}><div className={card}>
        <div className="flex items-center justify-between gap-2 mb-2">
          <div className="flex items-center gap-2"><AlertTriangle className="h-5 w-5 text-amber-400" />
            <h1 className="font-semibold">{t("oidc.tierTitle", "Nível de verificação insuficiente")}</h1></div>
          <LanguageSwitcher dark className="scale-90 origin-right" />
        </div>
        <p className="text-sm text-slate-400 mb-3">
          {t("oidc.tierBody", "Este aplicativo exige o nível {req}, mas sua biometria está em {cur}.")
            .replace("{req}", tierName(state.tier_required)).replace("{cur}", tierName(state.tier_current))}
        </p>
        {recs.length > 0 && (
          <div className="rounded-xl border border-amber-500/30 bg-amber-500/5 p-4 mb-4">
            <p className="text-xs uppercase tracking-wider text-amber-400 mb-2">{t("oidc.tierHow", "Como subir de nível")}</p>
            <ul className="space-y-1.5 text-sm text-slate-300">
              {recs.includes("silver") && <li>• {t("oidc.tierSilver", "Prata: peça a um administrador para confirmar sua biometria.")}</li>}
              {recs.includes("gold") && <li>• {t("oidc.tierGold", "Ouro: envie um documento nacional válido (CNH) na sua conta para verificação.")}</li>}
            </ul>
          </div>
        )}
        <button onClick={() => decide(false)} disabled={busy}
          className="w-full h-10 rounded-lg bg-slate-700 hover:bg-slate-600 text-sm font-medium">{t("oidc.backToApp", "Voltar ao aplicativo")}</button>
      </div></div>
    );
  }

  const failed = (status && status !== "passed") || (state && state.status === "failed");
  if (failed) {
    return (
      <div className={box}><div className={card}>
        <div className="flex items-center gap-2 mb-2"><AlertTriangle className="h-5 w-5 text-red-400" />
          <h1 className="font-semibold">{t("oidc.failedTitle", "Autenticação não concluída")}</h1></div>
        <p className="text-sm text-slate-400 mb-4">{t("oidc.failedBody", "A verificação facial não foi aprovada. Você pode retornar ao aplicativo e tentar novamente.")}</p>
        <button onClick={() => decide(false)} disabled={busy}
          className="w-full h-10 rounded-lg bg-slate-700 hover:bg-slate-600 text-sm font-medium">{t("oidc.backToApp", "Voltar ao aplicativo")}</button>
      </div></div>
    );
  }

  return (
    <div className={box}><div className={card}>
      <div className="flex items-center justify-between gap-2 mb-1">
        <div className="flex items-center gap-2">
          <ScanFace className="h-6 w-6 text-blue-400" />
          <h1 className="text-lg font-semibold">{t("oidc.title", "Autorizar acesso")}</h1>
        </div>
        <LanguageSwitcher dark className="scale-90 origin-right" />
      </div>
      <p className="text-sm text-slate-400 mb-5">
        {state?.person_name ? <><b className="text-slate-200">{state.person_name}</b>, </> : ""}
        {t("oidc.intro", "o aplicativo")}{" "}
        <b className="text-slate-200">{state?.client_name || t("oidc.client", "cliente")}</b>{" "}
        {t("oidc.introTail", "quer acessar sua identidade verificada por biometria.")}
      </p>

      {/* Ambiguous match → e-mail confirmation */}
      {state && state.needs_email_confirmation && !emailConfirmed && (
        <div className="mb-5 rounded-xl border border-amber-500/30 bg-amber-500/5 p-4">
          <div className="flex items-center gap-2 mb-1 text-amber-400 text-sm font-medium">
            <Mail className="h-4 w-4" /> {t("oidc.confirmTitle", "Confirmação de identidade")}
          </div>
          <p className="text-xs text-slate-400 mb-3">
            {t("oidc.confirmBody", "Para sua segurança, precisamos confirmar sua identidade por e-mail")}
            {state.email_masked ? <> ({state.email_masked})</> : null}.
          </p>
          {!otpSent ? (
            <button onClick={sendOtp} disabled={busy}
              className="w-full h-9 rounded-lg bg-amber-600 hover:bg-amber-500 text-sm font-medium disabled:opacity-60">
              {busy ? t("oidc.sending", "Enviando…") : t("oidc.sendCode", "Enviar código por e-mail")}
            </button>
          ) : (
            <div className="flex gap-2">
              <input value={otp} onChange={(e) => setOtp(e.target.value)} inputMode="numeric" maxLength={6}
                placeholder="000000"
                className="flex-1 h-9 rounded-lg bg-[#0e1013] border border-[#262d36] px-3 text-sm tracking-widest text-center" />
              <button onClick={verifyOtp} disabled={busy || otp.trim().length < 6}
                className="h-9 px-4 rounded-lg bg-amber-600 hover:bg-amber-500 text-sm font-medium disabled:opacity-60">{t("oidc.confirm", "Confirmar")}</button>
            </div>
          )}
        </div>
      )}
      {state && state.needs_email_confirmation && emailConfirmed && (
        <p className="mb-4 text-xs text-emerald-400 inline-flex items-center gap-1">
          <ShieldCheck className="h-4 w-4" /> {t("oidc.confirmed", "Identidade confirmada por e-mail.")}
        </p>
      )}

      {/* Scopes */}
      <div className="mb-5">
        <p className="text-xs uppercase tracking-wider text-slate-500 mb-2">{t("oidc.willShare", "Serão compartilhados")}</p>
        <ul className="space-y-1.5">
          {(state?.scopes || []).map((sc) => (
            <li key={sc} className="flex items-center gap-2 text-sm text-slate-300">
              <ShieldCheck className="h-4 w-4 text-blue-400" /> {scopeLabel[sc] || sc}
            </li>
          ))}
        </ul>
      </div>

      {error && <p className="text-sm text-red-400 mb-3">{t(error[0], error[1])}</p>}

      <div className="flex gap-2">
        <button onClick={() => decide(false)} disabled={busy}
          className="flex-1 h-11 rounded-lg bg-slate-700 hover:bg-slate-600 text-sm font-medium disabled:opacity-60">{t("oidc.deny", "Recusar")}</button>
        <button onClick={() => decide(true)}
          disabled={busy || (state?.needs_email_confirmation && !emailConfirmed)}
          className="flex-1 h-11 rounded-lg bg-blue-600 hover:bg-blue-500 text-sm font-semibold disabled:opacity-50">
          {busy ? "…" : t("oidc.authorize", "Autorizar")}
        </button>
      </div>
      <p className="mt-4 text-[11px] text-center text-slate-500">
        {state?.already_consented
          ? t("oidc.alreadyConsented", "Você já autorizou este aplicativo anteriormente.")
          : t("oidc.revokeNote", "Você pode revogar este acesso a qualquer momento.")}
      </p>
    </div></div>
  );
}
